import type { AdminPlanSpendBucket } from '@steado/admin-contract/plan-runs';
import { useQuery } from '@tanstack/react-query';

import { Panel, StatStrip, TabSkeleton } from '@/components/panel/panels';
import { useAuth } from '@/lib/auth/auth-context';
import { humanise } from '@/lib/format';
import { formatUsd } from '@/lib/format-spend';
import { TabError } from '@/screens/user-detail/TabError';

import { WINDOW_DAYS } from './DurationsPanel';
import { planSpendQueryOptions, type PlanRunKindFilter } from './plan-ops-query';

/**
 * What generation cost, split by what asked for it.
 *
 * AI operations already totals the model bill; the split this panel draws is
 * the one nobody could see — `weekly_cron` is spend the product decides to
 * make and `user_request` is spend a person asked for, and only one of those
 * can be turned off.
 *
 * The window's total is the headline, the table under it is only the split
 * and the runs and people behind the total close the card, so no figure is
 * drawn twice.
 *
 * A null `costUsd` is "no call in the bucket carried a price", not zero, and
 * `costUnknown` above zero makes the figure a floor — `formatUsd` renders both
 * as such, so no row here can read as exact when it is not.
 */
export function SpendPanel({ kind }: { readonly kind: PlanRunKindFilter }) {
  const { api } = useAuth();
  const query = useQuery(planSpendQueryOptions(api, WINDOW_DAYS, kind));
  const data = query.data;
  const spent = data !== undefined && data.overall.runs > 0;

  return (
    <Panel
      title="Generation spend"
      description={`Last ${String(WINDOW_DAYS)} days · by what asked for it`}
      action={
        data === undefined ? null : (
          <span className="flex flex-col items-end gap-px">
            {/* The hero figure, the same one `StatCards` draws. */}
            <span className="font-mono text-2xl leading-none font-medium tracking-[-0.025em] tabular-nums text-foreground">
              {formatUsd(data.overall.costUsd, data.overall.costUnknown > 0)}
            </span>
            {data.overall.costUnknown > 0 ? (
              <span className="text-xs text-warn-fg">a floor, not a total</span>
            ) : null}
          </span>
        )
      }
      footer={
        data === undefined || !spent ? undefined : (
          <StatStrip
            items={[
              { value: data.overall.runs, label: 'Runs' },
              { value: data.overall.users, label: 'People' },
              { value: data.overall.calls, label: 'Model calls' },
            ]}
          />
        )
      }
    >
      {query.isPending ? <TabSkeleton rows={2} /> : null}
      {query.isError ? <TabError message={query.error.message} onRetry={() => void query.refetch()} /> : null}

      {data === undefined ? null : !spent ? (
        <p className="text-[13px] text-muted-foreground">
          No generation spend in the last {WINDOW_DAYS} days.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[360px] border-collapse text-[13px]">
            <thead>
              {/* The console's column-header style, as `DataTable` draws it. */}
              <tr className="border-b border-border text-left text-[11.5px] font-semibold tracking-[0.055em] text-muted-foreground uppercase">
                <th className="py-2 pr-4">Trigger</th>
                <th className="py-2 pr-4 text-right">Runs</th>
                <th className="py-2 pr-4 text-right">People</th>
                <th className="py-2 text-right">Cost</th>
              </tr>
            </thead>
            <tbody>
              {data.byTrigger.map((row) => (
                <SpendRow key={row.trigger} label={humanise(row.trigger)} bucket={row} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}

interface SpendRowProps {
  readonly label: string;
  readonly bucket: AdminPlanSpendBucket;
}

function SpendRow({ label, bucket }: SpendRowProps) {
  return (
    <tr className="border-b border-border last:border-0">
      <td className="py-2 pr-4 text-muted-foreground">{label}</td>
      <td className="py-2 pr-4 text-right tabular-nums text-foreground">{bucket.runs}</td>
      <td className="py-2 pr-4 text-right tabular-nums text-foreground">{bucket.users}</td>
      <td className="py-2 text-right font-mono tabular-nums text-foreground">
        {formatUsd(bucket.costUsd, bucket.costUnknown > 0)}
      </td>
    </tr>
  );
}
